/**
 * Medições por entrega (nos dois sentidos), custos/CAPEX e valor agregado.
 * Regras combinadas com a VMM:
 *  - medição por entrega, com valores; aprovação do gerente do projeto e depois do diretor de engenharia;
 *  - pagamento em até 30 dias após a emissão da nota fiscal (configurável por contrato);
 *  - vale tanto para a VMM medindo as contratadas (a pagar) quanto para a VMM medindo o cliente (a receber);
 *  - medição aprovada fica congelada: correção só por nova medição (com valor negativo, se preciso).
 */
import { addDays, diffDays } from './dates.js';
import { ROLE_KEYS, ROLES } from './permissions.js';
export const DIRECTION_LABEL = { receber: 'A receber (VMM mede o cliente)', pagar: 'A pagar (contratada mede a VMM)' };
export const MEAS_STATUSES = ['Rascunho', 'Aguardando gerente', 'Aguardando diretor', 'Aprovada', 'Faturada', 'Paga', 'Cancelada'];
export const MEAS_ACTION_LABEL = {
    enviar: 'Enviar para aprovação', aprovar_gerente: 'Aprovar (gerente)', aprovar_diretor: 'Aprovar (diretor)', devolver: 'Devolver para correção',
    faturar: 'Registrar nota fiscal', pagar: 'Registrar pagamento', cancelar: 'Cancelar medição'
};
/** quem prepara e registra nota/pagamento */
export const MEAS_EDITORS = new Set(['gerente', 'diretor_fin']);
/** Ações possíveis para o perfil na situação atual. */
export function measActions(status, role) {
    if (!role)
        return [];
    const ed = MEAS_EDITORS.has(role);
    const out = [];
    if (status === 'Rascunho' && ed)
        out.push('enviar', 'cancelar');
    if (status === 'Aguardando gerente' && role === 'gerente')
        out.push('aprovar_gerente', 'devolver');
    if (status === 'Aguardando diretor' && role === 'diretor')
        out.push('aprovar_diretor', 'devolver');
    if (status === 'Aprovada' && ed)
        out.push('faturar');
    if (status === 'Faturada' && ed)
        out.push('pagar');
    return out;
}
export function measNext(status, action) {
    switch (action) {
        case 'enviar': return 'Aguardando gerente';
        case 'aprovar_gerente': return 'Aguardando diretor';
        case 'aprovar_diretor': return 'Aprovada';
        case 'devolver': return 'Rascunho';
        case 'faturar': return 'Faturada';
        case 'pagar': return 'Paga';
        case 'cancelar': return 'Cancelada';
    }
    return status;
}
const MEAS_ORDER = ['enviar', 'aprovar_gerente', 'aprovar_diretor', 'faturar', 'pagar', 'devolver', 'cancelar'];
const MEAS_WHO = (a, status) => a === 'aprovar_gerente' || (a === 'devolver' && status === 'Aguardando gerente') ? 'do gerente do projeto'
    : a === 'aprovar_diretor' || (a === 'devolver' && status === 'Aguardando diretor') ? 'do diretor de engenharia' : 'do gerente do projeto ou do diretor financeiro';
/** Todas as ações da situação, com o motivo de bloqueio para quem não pode fazê-las (para mostrar o botão desabilitado). */
export function measActionOptions(status, role) {
    const mine = new Set(measActions(status, role));
    const all = new Set(ROLE_KEYS.flatMap(r => measActions(status, r)));
    return MEAS_ORDER.filter(a => all.has(a)).map(a => ({ action: a, allowed: mine.has(a), reason: mine.has(a) ? null : `Esta etapa é ${MEAS_WHO(a, status)}. O seu perfil${role ? ` (${ROLES[role]})` : ''} não pode fazer isto.` }));
}
export const MEAS_FROZEN = (s) => s !== 'Rascunho';
/** Medições que contam como valor medido (aprovadas em diante). */
export const MEAS_COUNTS = (s) => s === 'Aprovada' || s === 'Faturada' || s === 'Paga';
export const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
/** Verifica as linhas de uma medição contra o contratado e o já medido (aprovado ou em aprovação) das outras medições. */
export function checkMeasurement(items, lines, measuredBefore) {
    const byId = new Map(items.map(i => [i.id, i]));
    const errors = [];
    const seen = new Set();
    const out = lines.map(l => {
        const it = byId.get(l.itemId);
        if (!it) {
            errors.push('Item não pertence ao contrato.');
            return { ...l, amount: 0, accumulated: 0, balance: 0 };
        }
        if (seen.has(l.itemId))
            errors.push(`Item ${it.code} repetido na medição.`);
        seen.add(l.itemId);
        if (!Number.isFinite(l.quantity) || l.quantity === 0)
            errors.push(`Item ${it.code}: informe a quantidade medida.`);
        const before = measuredBefore.get(it.id) ?? 0;
        const acc = before + l.quantity;
        if (acc > it.quantity + 1e-9)
            errors.push(`Item ${it.code}: o acumulado (${fmt(acc)} ${it.unit}) passa do contratado (${fmt(it.quantity)} ${it.unit}).`);
        if (acc < -1e-9)
            errors.push(`Item ${it.code}: o acumulado ficaria negativo.`);
        return { ...l, amount: round2(l.quantity * it.unitPrice), accumulated: acc, balance: it.quantity - acc };
    });
    return { errors, total: round2(out.reduce((s, l) => s + l.amount, 0)), lines: out };
}
const fmt = (n) => n.toLocaleString('pt-BR', { maximumFractionDigits: 3 });
export function retentionOf(total, retentionPercent) { return round2(total * (retentionPercent || 0) / 100); }
export function dueDateOf(invoiceDate, termDays = 30) { return addDays(invoiceDate, termDays); }
export function dueState(m, today, soonDays = 5) {
    if (m.status === 'Paga')
        return { state: 'paga', days: null };
    if (!m.dueDate || m.status !== 'Faturada')
        return { state: 'sem nota', days: null };
    const d = diffDays(today, m.dueDate);
    return { state: d < 0 ? 'vencida' : d <= soonDays ? 'vence logo' : 'a vencer', days: d };
}
/* ------------------------------------------------------------- custos */
export const COST_CATEGORIES = ['Engenharia', 'Equipamentos', 'Materiais', 'Construção', 'Montagem', 'Gerenciamento', 'Comissionamento', 'Contingência'];
/**
 * Fórmulas (por conta de custo):
 *  comprometido = soma dos contratos a pagar ligados à conta;
 *  realizado (incorrido) = lançamentos realizados + medições a pagar aprovadas da conta;
 *  pago = parte do realizado com data de pagamento;  incorrido não pago = realizado - pago;
 *  ETC = valor informado, ou o maior entre (orçamento revisado - realizado) e o saldo dos contratos (comprometido - já medido), nunca negativo;
 *  EAC = realizado + ETC;  desvio = orçamento revisado - EAC (negativo é estouro).
 */
export function costSummary(accounts, contracts, entries, meas) {
    const accOfContract = new Map(contracts.map(c => [c.id, c.costAccountId]));
    const list = accounts.map(a => {
        const committed = contracts.filter(c => c.direction === 'pagar' && c.costAccountId === a.id).reduce((s, c) => s + c.value, 0);
        const ent = entries.filter(e => e.accountId === a.id && e.kind === 'Realizado');
        const mm = meas.filter(m => m.direction === 'pagar' && MEAS_COUNTS(m.status) && accOfContract.get(m.contractId) === a.id);
        const actual = ent.reduce((s, e) => s + e.amount, 0) + mm.reduce((s, m) => s + m.total, 0);
        const paid = ent.filter(e => e.paidDate).reduce((s, e) => s + e.amount, 0) + mm.filter(m => m.status === 'Paga').reduce((s, m) => s + m.total, 0);
        const measuredOnContracts = mm.reduce((s, m) => s + m.total, 0);
        const etc = a.etcManual !== null && a.etcManual !== undefined ? a.etcManual : Math.max(0, a.budgetRevised - actual, committed - measuredOnContracts);
        const eac = actual + etc;
        return { id: a.id, code: a.code, name: a.name, category: a.category, budgetApproved: a.budgetApproved, budgetRevised: a.budgetRevised, committed: round2(committed), actual: round2(actual), paid: round2(paid), unpaid: round2(actual - paid), etc: round2(etc), eac: round2(eac), variance: round2(a.budgetRevised - eac), overrun: eac > a.budgetRevised + 0.005 };
    });
    const sum = (k) => round2(list.reduce((s, a) => s + a[k], 0));
    const totals = { budgetApproved: sum('budgetApproved'), budgetRevised: sum('budgetRevised'), committed: sum('committed'), actual: sum('actual'), paid: sum('paid'), unpaid: sum('unpaid'), etc: sum('etc'), eac: sum('eac'), variance: sum('variance'), contingency: round2(list.filter(a => a.category === 'Contingência').reduce((s, a) => s + a.budgetRevised - a.actual, 0)), overrun: false };
    totals.overrun = totals.eac > totals.budgetRevised + 0.005;
    const byCategory = COST_CATEGORIES.map(category => { const l = list.filter(a => a.category === category); return { category, budgetRevised: round2(l.reduce((s, a) => s + a.budgetRevised, 0)), actual: round2(l.reduce((s, a) => s + a.actual, 0)), eac: round2(l.reduce((s, a) => s + a.eac, 0)) }; }).filter(c => c.budgetRevised || c.actual || c.eac);
    return { accounts: list, totals, byCategory };
}
/** Fluxo de caixa mensal (desembolso): previsto = lançamentos previstos + medições a pagar pelo vencimento; realizado = pagamentos. */
export function cashFlow(entries, meas, months) {
    const m = (d) => d ? d.slice(0, 7) : null;
    return months.map(month => ({
        month,
        planned: round2(entries.filter(e => e.kind === 'Previsto' && m(e.date) === month).reduce((s, e) => s + e.amount, 0) + meas.filter(x => x.direction === 'pagar' && MEAS_COUNTS(x.status) && m(x.dueDate ?? x.approvedAt) === month).reduce((s, x) => s + x.total, 0)),
        actual: round2(entries.filter(e => e.kind === 'Realizado' && m(e.paidDate) === month).reduce((s, e) => s + e.amount, 0) + meas.filter(x => x.direction === 'pagar' && x.status === 'Paga' && m(x.paidDate) === month).reduce((s, x) => s + x.total, 0)),
        receivable: round2(meas.filter(x => x.direction === 'receber' && MEAS_COUNTS(x.status) && m(x.dueDate ?? x.approvedAt) === month).reduce((s, x) => s + x.total, 0)),
        received: round2(meas.filter(x => x.direction === 'receber' && x.status === 'Paga' && m(x.paidDate) === month).reduce((s, x) => s + x.total, 0))
    }));
}
/* ------------------------------------------------------------- riscos */
export const RISK_KINDS = ['Risco', 'Problema', 'Oportunidade', 'Pendência'];
export const RISK_STATUSES = ['Aberto', 'Em tratamento', 'Encerrado'];
export const RISK_IMPACTS = [['impactSchedule', 'Prazo'], ['impactCost', 'Custo'], ['impactSafety', 'Segurança'], ['impactQuality', 'Qualidade'], ['impactOperation', 'Operação'], ['impactEnvironment', 'Meio ambiente']];
export const PROB_LABEL = ['', 'Muito baixa', 'Baixa', 'Média', 'Alta', 'Muito alta'];
export const IMPACT_LABEL = ['Nenhum', 'Muito baixo', 'Baixo', 'Médio', 'Alto', 'Muito alto'];
/** Criticidade = probabilidade (1 a 5) × maior impacto (1 a 5). Problemas já ocorreram: probabilidade 5. */
export function riskScore(r) {
    const impact = Math.max(0, ...RISK_IMPACTS.map(([k]) => Number(r[k] ?? 0)));
    const p = r.kind === 'Problema' ? 5 : Math.min(5, Math.max(1, Number(r.probability) || 1));
    const score = p * impact;
    return { impact, score, level: levelOfScore(score) };
}
export const levelOfScore = (s) => s >= 15 ? 'Crítico' : s >= 8 ? 'Alto' : s >= 4 ? 'Médio' : 'Baixo';
/* ------------------------------------------------------------- mudanças */
export const CHANGE_STATUSES = ['Rascunho', 'Em análise', 'Aguardando decisão', 'Aprovada', 'Rejeitada', 'Implementada', 'Cancelada'];
export const CHANGE_ACTION_LABEL = { enviar_analise: 'Enviar para análise', enviar_decisao: 'Enviar para decisão', aprovar: 'Aprovar', rejeitar: 'Rejeitar', implementar: 'Registrar implementação', cancelar: 'Cancelar', reabrir: 'Voltar para análise' };
/** Precisa do diretor quando mexe em custo ou prazo. */
export const changeNeedsDirector = (c) => Math.abs(Number(c.impactCapex ?? 0)) > 0 || Math.abs(Number(c.impactScheduleDays ?? 0)) > 0;
/** Todas as ações da mudança na situação atual, com o motivo de bloqueio. */
export function changeActionOptions(c, role, canEdit) {
    const mine = new Set(changeActions(c, role, canEdit));
    const all = [];
    for (const r of ROLE_KEYS)
        for (const a of changeActions(c, r, true))
            if (!all.includes(a))
                all.push(a);
    const dir = changeNeedsDirector(c) && !!c.managerApprovedAt;
    return all.map(a => ({ action: a, allowed: mine.has(a), reason: mine.has(a) ? null
            : a === 'aprovar' || a === 'rejeitar' ? `A decisão agora é ${dir ? 'do diretor de engenharia (o gerente já aprovou)' : 'do gerente do projeto'}.`
                : `Esta ação é de quem edita as mudanças do projeto. O seu perfil${role ? ` (${ROLES[role]})` : ''} só consulta.` }));
}
export function changeActions(c, role, canEdit) {
    const out = [];
    if (c.status === 'Rascunho' && canEdit)
        out.push('enviar_analise', 'cancelar');
    if (c.status === 'Em análise' && canEdit)
        out.push('enviar_decisao', 'cancelar');
    if (c.status === 'Aguardando decisão') {
        const needDir = changeNeedsDirector(c);
        if (role === 'gerente' && !c.managerApprovedAt)
            out.push('aprovar', 'rejeitar');
        if (role === 'diretor' && needDir && c.managerApprovedAt)
            out.push('aprovar', 'rejeitar');
        if (canEdit)
            out.push('reabrir');
    }
    if (c.status === 'Aprovada' && canEdit)
        out.push('implementar');
    return out;
}
//# sourceMappingURL=finance.js.map